import { Fragment, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Badge, Button, Card, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { USAGE_ACTION } from '../../constants/sample.constants';
import type { UsageRecordDayGroup, UsageRecordDayRow } from '../../services/report.service';
import {
  softBreakIdentifier,
  formatEntityDisplayName,
} from '../../utils/softBreakIdentifier';
import { syncUsageDayTableColgroup } from '../../utils/syncDataTableColgroup';
import {
  computeUsageRecordDaySummary,
  formatDayUsedQuantityLabel,
  formatDayUsedQuantityValue,
  formatUsageRecordDetail,
  formatUsageRecordLocation,
  formatUsageRecordMobilePrimary,
  formatUsageRecordTime,
} from '../../utils/usageRecordsDayDisplay';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';
import TextContentSep from '../common/TextContentSep';
import { formatIsoDateForDisplay } from './BaseDateField';
import { SamplePanelCardHeader } from './SamplePanelCardHeader';

const COL_COUNT = 9;

export type UsageRecordsDayTableProps = {
  days: UsageRecordDayGroup[];
  expandedDays: Set<string>;
  onToggleDay: (date: string) => void;
  onOpenSample: (sampleId: number) => void;
  renderEditCell: (row: UsageRecordDayRow) => ReactNode;
  /** パネル見出し右（件数バッジ・展開トグル等） */
  headerTrailing?: ReactNode;
};

/** 使用履歴の日付グループ表（xs は CSS Grid カード） */
export default function UsageRecordsDayTable({
  days,
  expandedDays,
  onToggleDay,
  onOpenSample,
  renderEditCell,
  headerTrailing,
}: UsageRecordsDayTableProps) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const quantityZeroLabel = t('usage_records.quantity_zero_display', {
    defaultValue: '0（確認のみ）',
  });

  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const sync = () => syncUsageDayTableColgroup(table);
    sync();
    const host = table.parentElement ?? table;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(host);
    window.addEventListener('resize', sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [days, expandedDays]);

  return (
    <Card className="mb-2 shadow-sm border-0 sample-panel-card">
      <SamplePanelCardHeader
        title={
          <span className="visually-hidden">
            {t('usage_records.list_title', { defaultValue: '記録一覧' })}
          </span>
        }
        trailing={headerTrailing}
      />
      <Card.Body className="p-0">
        <div className="table-responsive ur-table-wrap">
          <Table
            ref={tableRef}
            hover
            className="mb-0 align-middle ur-day-table"
            size="sm"
          >
            <thead className="table-light">
              <tr>
                <th className="text-nowrap ur-time-col">
                  {t('usage_records.col_time')}
                </th>
                <th className="text-nowrap ur-name-col">
                  {t('sample.sample_name')}
                </th>
                <th className="text-nowrap ur-action-col">
                  {t('usage_records.col_action')}
                </th>
                <th className="text-end text-nowrap ur-qty-col">
                  {t('usage_records.col_quantity')}
                </th>
                <th
                  className="text-end text-nowrap ur-remain-col"
                  title={t('sample.remaining_quantity')}
                >
                  {t('sample.remaining_quantity_short', { defaultValue: '残' })}
                </th>
                <th
                  className="text-nowrap ur-location-col"
                  title={t('usage_records.col_location_title', {
                    defaultValue: '保管庫・引き出し・ケース',
                  })}
                >
                  {t('usage_records.col_location')}
                </th>
                <th className="text-nowrap ur-detail-col">
                  {t('usage_records.col_detail')}
                </th>
                <th className="d-none d-xl-table-cell text-nowrap ur-hide-mobile ur-recorded-by-col">
                  {t('usage_records.col_recorded_by')}
                </th>
                <th className="text-center text-nowrap ur-edit-col">
                  <span className="visually-hidden">{t('common.edit')}</span>
                  <i className="fas fa-pen text-muted" aria-hidden="true" title={t('common.edit')} />
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => {
                const summary = computeUsageRecordDaySummary(day.records);
                const isExpanded = expandedDays.has(day.date);
                const dateLabel = formatIsoDateForDisplay(day.date);
                const usedQtyLabel = formatDayUsedQuantityLabel(
                  summary.usedQuantity,
                  summary.sharedUnit,
                  t
                );
                const usedQtyValue = formatDayUsedQuantityValue(
                  summary.usedQuantity,
                  summary.sharedUnit,
                  t
                );
                const qtyBadgeClass =
                  summary.usedQuantity > 0
                    ? 'flex-shrink-0 ur-day-qty-badge ur-day-qty-badge--active'
                    : 'flex-shrink-0 ur-day-qty-badge';
                return (
                  <Fragment key={day.date}>
                    <tr className="table-light ur-day-header">
                      <td colSpan={COL_COUNT} className="p-0">
                        <button
                          type="button"
                          className="btn btn-link text-decoration-none text-body w-100 text-start py-1 px-2 rounded-0 ur-day-header-btn"
                          aria-expanded={isExpanded}
                          // 可視は日付＋件数。名前は短く、開閉は title
                          aria-label={`${dateLabel} · ${t('usage_records.day_record_count', {
                            count: summary.recordCount,
                          })}`}
                          title={t(
                            isExpanded
                              ? 'usage_records.collapse_day'
                              : 'usage_records.expand_day',
                            { date: dateLabel }
                          )}
                          onClick={() => onToggleDay(day.date)}
                        >
                          <div
                            className="d-flex align-items-center flex-nowrap gap-2 ur-day-header-inner"
                            aria-hidden="true"
                          >
                            <strong className="text-nowrap ur-day-header-date">
                              <i
                                className="fas fa-calendar-day me-1 text-primary d-none d-sm-inline"
                                aria-hidden="true"
                              />
                              {dateLabel}
                            </strong>
                            <TextContentSep />
                            <Badge bg="secondary" className="flex-shrink-0">
                              {t('usage_records.day_record_count', {
                                count: summary.recordCount,
                              })}
                            </Badge>
                            {summary.uniqueSampleCount !== summary.recordCount ? (
                              <>
                                <TextContentSep />
                                <Badge
                                  bg="light"
                                  text="dark"
                                  className="flex-shrink-0 d-none d-sm-inline-block"
                                >
                                  {t('usage_records.day_sample_count', {
                                    count: summary.uniqueSampleCount,
                                  })}
                                </Badge>
                              </>
                            ) : null}
                            {summary.usedQuantity > 0 ? (
                              <>
                                <TextContentSep />
                                <Badge
                                  bg="info"
                                  text="dark"
                                  className={qtyBadgeClass}
                                  title={t('usage_records.day_used_quantity_long', {
                                    count: summary.usedQuantity,
                                    quantity: usedQtyValue,
                                    defaultValue: joinLabeledValue(
                                      t('usage_records.col_quantity', {
                                        defaultValue: '使用数量',
                                      }),
                                      usedQtyValue
                                    ),
                                  })}
                                >
                                  {usedQtyLabel}
                                </Badge>
                              </>
                            ) : null}
                            <span
                              className="ms-auto text-muted small flex-shrink-0"
                              aria-hidden="true"
                            >
                              <i
                                className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}
                              />
                            </span>
                          </div>
                        </button>
                      </td>
                    </tr>
                    {isExpanded &&
                      day.records.map((row) => {
                        const location = formatUsageRecordLocation(row);
                        const detail = formatUsageRecordDetail(row);
                        const mobilePrimary = formatUsageRecordMobilePrimary(
                          row,
                          quantityZeroLabel,
                          t
                        );
                        const detailLine = detail !== '-' ? detail : null;
                        const nameLabel = formatEntityDisplayName(row.sampleName, t);
                        const nameIdentity = [
                          nameLabel,
                          `#${row.sampleId}`,
                          mobilePrimary,
                          location,
                          detailLine,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <tr key={row.id} className="ur-record-row">
                            <td className="text-nowrap ur-time-col">
                              {formatUsageRecordTime(row.usageDate)}
                            </td>
                            <td className="ur-name-col">
                              <div className="ur-name-stack">
                                {/* xs: メタもボタン内（.ur-name-meta は pointer-events:none 前提） */}
                                <Button
                                  variant="link"
                                  className="text-start text-decoration-none ur-name-link"
                                  title={nameIdentity}
                                  aria-label={nameIdentity}
                                  onClick={() => onOpenSample(row.sampleId)}
                                >
                                  <span className="ur-name-text">
                                    {softBreakIdentifier(row.sampleName, t)}
                                    <span
                                      className="text-muted small text-nowrap ur-name-id-desktop d-none d-md-inline"
                                      aria-hidden="true"
                                    >
                                      {' · '}#{row.sampleId}
                                    </span>
                                    {/* xs のみメタ前の ·（md+ は #id を名前横に出す） */}
                                    {(mobilePrimary || location || detailLine) ? (
                                      <span className="d-md-none">{' · '}</span>
                                    ) : null}
                                  </span>
                                  <span className="ur-name-meta d-md-none">
                                    {mobilePrimary ? (
                                      <span className="ur-mobile-primary text-muted">
                                        {mobilePrimary}
                                      </span>
                                    ) : null}
                                    {location ? (
                                      <span className="ur-mobile-secondary" title={location}>
                                        {mobilePrimary ? ' · ' : null}
                                        {softBreakIdentifier(location)}
                                      </span>
                                    ) : null}
                                    {detailLine ? (
                                      <span className="ur-mobile-note" title={detailLine}>
                                        {mobilePrimary || location ? ' · ' : null}
                                        {softBreakIdentifier(detailLine)}
                                      </span>
                                    ) : null}
                                  </span>
                                </Button>
                              </div>
                            </td>
                            <td className="text-nowrap ur-action-col">{row.action || '-'}</td>
                            <td
                              className="text-end text-nowrap ur-qty-col"
                              title={
                                row.action !== USAGE_ACTION.CREATED && row.quantity === 0
                                  ? t('usage_record.quantity_note')
                                  : undefined
                              }
                            >
                              {row.action === USAGE_ACTION.CREATED
                                ? '-'
                                : row.quantity === 0
                                  ? quantityZeroLabel
                                  : formatSampleQuantityWithUnit(
                                      row.quantity,
                                      row.unit,
                                      t
                                    )}
                            </td>
                            <td
                              className="text-end text-nowrap ur-remain-col"
                              title={t('sample.remaining_quantity')}
                            >
                              {formatSampleQuantityWithUnit(
                                row.remainingQuantity,
                                row.unit,
                                t
                              )}
                            </td>
                            <td className="ur-location-col" title={location || undefined}>
                              <span className="ur-location-text">
                                {location ? softBreakIdentifier(location) : '-'}
                              </span>
                            </td>
                            <td className="ur-detail-col text-break" title={detail}>
                              {softBreakIdentifier(detail)}
                            </td>
                            <td className="d-none d-xl-table-cell text-nowrap ur-hide-mobile ur-recorded-by-col">
                              {row.recordedBy || row.creatorName || '-'}
                            </td>
                            <td className="text-center ur-edit-col">{renderEditCell(row)}</td>
                          </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}
